<script setup lang="ts">
// Vue
import { computed, ref, watch } from "vue";

// Dependencies
import { useToast } from "vue-toastification";

// Stores / providers
import { useModalStore } from "@/stores/modals/modals.store";
import { useEmissionFactorSources } from "@/dataProviders/EmissionFactorSources";

// GraphQL
import {
  Emission_Factor_Type_Enum,
  GetProjectEmissionFactorCustomSourcesDocument,
  useDeleteProjectEmissionFactorCustomMutation,
  useGetProjectEmissionFactorCustomSourcesQuery,
  useInsertProjectEmissionFactorCustomMutation,
  useUpdateProjectEmissionFactorCustomMutation,
} from "@/graphql";
import type { GetProjectEmissionFactorCustomSourcesQuery } from "@/graphql";

// Components
import { Pencil, Trash2 } from "@lucide/vue";
import Button from "@/components/Reusables/Buttons/Button.vue";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import TextFieldInput from "@/components/Reusables/Inputs/TextFieldInput.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";

type CustomRow = GetProjectEmissionFactorCustomSourcesQuery["projectEmissionFactorCustom"][number];

// `id` is null while drafting a new row, or the existing row's id when editing.
type DraftForm = {
  id: string | null;
  name: string;
  description: string;
  emissionFactorType: Emission_Factor_Type_Enum | "";
  emissionValue: number | null;
};

// Excludes LEGACY, WASTAGE, and EXPORTED_ELECTRICITY, which are system-managed
// or used by other flows and aren't user-selectable here.
const SELECTABLE_TYPES: readonly Emission_Factor_Type_Enum[] = [
  Emission_Factor_Type_Enum.ELECTRICITY,
  Emission_Factor_Type_Enum.HEATING,
  Emission_Factor_Type_Enum.DISTRICT_HEATING,
  Emission_Factor_Type_Enum.LINE_GAS,
  Emission_Factor_Type_Enum.DIESEL,
  Emission_Factor_Type_Enum.BIODIESEL,
  Emission_Factor_Type_Enum.GASOLINE,
  Emission_Factor_Type_Enum.BLACK_OIL,
  Emission_Factor_Type_Enum.GAS,
  Emission_Factor_Type_Enum.METHANE,
  Emission_Factor_Type_Enum.EARTH_MOVED,
];

const formatEmissionFactorType = (enumValue: string): string =>
  enumValue
    .split("_")
    .map((word, index) => (index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
    .join(" ");

const emptyDraft = (): DraftForm => ({
  id: null,
  name: "",
  description: "",
  emissionFactorType: "",
  emissionValue: null,
});

const toast = useToast();
const modals = useModalStore();
const emissionFactorSources = useEmissionFactorSources();

const { result, loading, error, refetch, onResult } = useGetProjectEmissionFactorCustomSourcesQuery({
  fetchPolicy: "no-cache",
});

const rows = computed<CustomRow[]>(() => {
  const list = result.value?.projectEmissionFactorCustom ?? [];
  // Hides soft-deleted rows here only; EmissionFactorSourcesProvider still gets the
  // full list so referenced projects can render "<name> (deleted)".
  return [...list].filter((row) => !row.deletedAt).sort((a, b) => a.name.localeCompare(b.name));
});

// Pushes every fresh result into EmissionFactorSourcesProvider so any open
// DataSourceCombobox elsewhere reflects changes without waiting for a re-mount.
onResult((res) => {
  if (res.data) emissionFactorSources.mapCustomData(res.data);
});

const refetchAndPropagate = async () => {
  const fresh = await refetch();
  if (fresh?.data) emissionFactorSources.mapCustomData(fresh.data);
};

const { mutate: insertMutation, loading: insertLoading } = useInsertProjectEmissionFactorCustomMutation({
  fetchPolicy: "no-cache",
});

const { mutate: updateMutation, loading: updateLoading } = useUpdateProjectEmissionFactorCustomMutation({
  fetchPolicy: "no-cache",
});

const { mutate: deleteMutation, loading: deleteLoading } = useDeleteProjectEmissionFactorCustomMutation({
  fetchPolicy: "no-cache",
  // Satisfies Apollo's cache; the explicit refetchAndPropagate() call afterwards
  // is what actually drives the UI refresh.
  refetchQueries: [{ query: GetProjectEmissionFactorCustomSourcesDocument }],
});

const isMutating = computed(() => insertLoading.value || updateLoading.value || deleteLoading.value);

// At most one row is in edit mode at a time; `editing` is null when nothing is
// being created or edited.
const editing = ref<DraftForm | null>(null);
const wasValidated = ref<boolean>(false);

const isEditingNew = computed(() => editing.value !== null && editing.value.id === null);

const startCreate = () => {
  editing.value = emptyDraft();
  wasValidated.value = false;
};

const startEdit = (row: CustomRow) => {
  editing.value = {
    id: row.id,
    name: row.name,
    description: row.description ?? "",
    emissionFactorType: row.emissionFactorType,
    emissionValue: typeof row.emissionValue === "number" ? row.emissionValue : Number(row.emissionValue),
  };
  wasValidated.value = false;
};

const cancelEdit = () => {
  editing.value = null;
  wasValidated.value = false;
};

const nameError = computed(() => {
  if (!wasValidated.value || !editing.value) return undefined;
  return editing.value.name.trim().length === 0 ? "Please enter a name" : undefined;
});

const typeError = computed(() => {
  if (!wasValidated.value || !editing.value) return undefined;
  return editing.value.emissionFactorType === "" ? "Please choose a type" : undefined;
});

const emissionValueError = computed(() => {
  if (!wasValidated.value || !editing.value) return undefined;
  const value = editing.value.emissionValue;
  if (value === null || Number.isNaN(value)) return "Please enter a valid emission factor";
  if (value <= 0) return "Emission factor must be greater than zero";
  return undefined;
});

const hasFormErrors = computed(() => Boolean(nameError.value || typeError.value || emissionValueError.value));

const submitEdit = async () => {
  if (!editing.value) return;
  wasValidated.value = true;
  if (hasFormErrors.value) return;

  const payload = {
    name: editing.value.name.trim(),
    // Empty description gets sent as null so the column is nullable and the
    // input "" doesn't end up persisted as an empty string.
    description: editing.value.description.trim().length > 0 ? editing.value.description.trim() : null,
    emissionFactorType: editing.value.emissionFactorType as Emission_Factor_Type_Enum,
    emissionValue: editing.value.emissionValue ?? 0,
  };

  try {
    if (editing.value.id === null) {
      await insertMutation(payload);
      toast.success(`Custom data source "${payload.name}" created`);
    } else {
      await updateMutation({ id: editing.value.id, ...payload });
      toast.success(`Custom data source "${payload.name}" updated`);
    }
    await refetchAndPropagate();
    editing.value = null;
    wasValidated.value = false;
  } catch (err) {
    console.error("Failed to save custom data source", err);
    toast.error("Failed to save custom data source");
  }
};

const confirmDelete = async (row: CustomRow) => {
  // Soft-delete only: deletion is allowed even when in use, since referencing
  // projects keep the row and render it as "<name> (deleted)".
  const usage = usageCountFor(row);
  const message =
    usage > 0
      ? `Delete "${row.name}"? It will be hidden from data source pickers, but ${usage} project${
          usage === 1 ? "" : "s"
        } that already use it will keep showing it as "(deleted)".`
      : `Delete "${row.name}"? It will be hidden from data source pickers.`;

  const result = (await modals.open(
    "DeleteModal",
    {
      itemName: row.name,
      itemType: "Custom data source",
      message,
    },
    { stackable: true }
  )) as { action?: "confirm" | "cancel" } | undefined;

  if (result?.action !== "confirm") return;

  try {
    await deleteMutation({ id: row.id, deletedAt: new Date().toISOString() });
    await refetchAndPropagate();
    toast.success(`Custom data source "${row.name}" deleted`);
    // If the row being edited was the one deleted (edge case: user pressed
    // pencil and then delete on the same row), bail out of the edit form.
    if (editing.value?.id === row.id) cancelEdit();
  } catch (err) {
    console.error("Failed to delete custom data source", err);
    toast.error("Failed to delete custom data source.");
  }
};

const usageCountFor = (row: CustomRow): number => row.projectEmissionFactors_aggregate?.aggregate?.count ?? 0;

const isRowEditing = (row: CustomRow) => editing.value?.id === row.id;

// Disables Add/Edit/Delete on other rows while a form is open, so users can't
// abandon an in-progress draft by switching rows.
const otherActionsDisabled = computed(() => editing.value !== null || isMutating.value);

// If a refetch removes the row currently being edited (e.g. another tab
// deleted it), drop out of edit mode rather than silently editing a ghost.
watch(rows, (newRows) => {
  if (editing.value?.id && !newRows.some((r) => r.id === editing.value!.id)) {
    cancelEdit();
  }
});
</script>

<template>
  <div class="gap-px-16 flex flex-col">
    <header class="gap-px-4 flex flex-col">
      <h2 class="t-h3">Manage custom data sources</h2>
      <p class="t-small text-type-muted">
        Tenant-wide custom emission factor sources. Available to every project in this tenant — once created, they show up in
        every Data Source picker for the matching supply type.
      </p>
    </header>

    <div class="flex justify-end">
      <Button
        label="New custom data source"
        color="primary"
        plus
        :disabled="otherActionsDisabled"
        @click="startCreate" />
    </div>

    <LoadingWrapper
      :loading="loading"
      :error="!!error">
      <div class="gap-px-8 flex flex-col">
        <!-- New-row draft (renders above the list) -->
        <div
          v-if="isEditingNew && editing"
          class="rounded-8 border-app-blue-600 bg-app-blue-50 p-px-16 gap-px-12 flex flex-col border"
          data-cy="custom-data-source-draft">
          <div class="gap-px-12 grid grid-cols-12">
            <TextInputNew
              v-model="editing.name"
              class="col-span-12 sm:col-span-6 lg:col-span-4"
              label="Name"
              :required="true"
              :error-msg="nameError" />
            <SelectInputNew
              v-model="editing.emissionFactorType"
              class="col-span-12 sm:col-span-6 lg:col-span-4"
              label="Type"
              placeholder="Select type"
              :required="true"
              :error-msg="typeError">
              <option
                v-for="enumValue in SELECTABLE_TYPES"
                :key="enumValue"
                :value="enumValue">
                {{ formatEmissionFactorType(enumValue) }}
              </option>
            </SelectInputNew>
            <NumberInput
              :value="editing.emissionValue"
              class="col-span-12 sm:col-span-6 lg:col-span-4"
              label="Emission value (kg CO₂ eq / kWh)"
              color="primary"
              :positive="true"
              :required="true"
              :align-right="true"
              :error-msg="emissionValueError"
              @update:value="(v: number | null) => editing && (editing.emissionValue = v)" />
            <div class="col-span-12">
              <TextFieldInput
                :value="editing.description"
                label="Description"
                color="primary"
                placeholder="Optional description"
                size="min-h-20 w-full"
                @update:value="(v: string) => editing && (editing.description = v)" />
            </div>
          </div>
          <div class="gap-px-8 flex justify-end">
            <Button
              label="Cancel"
              color="button"
              :disabled="isMutating"
              @click="cancelEdit" />
            <Button
              label="Create"
              color="primary"
              :is-loading="insertLoading"
              :disabled="isMutating"
              @click="submitEdit" />
          </div>
        </div>

        <!-- Empty state -->
        <p
          v-if="rows.length === 0 && !isEditingNew"
          class="t-small text-type-muted py-px-32 text-center">
          No custom data sources yet — click "New custom data source" to create one.
        </p>

        <!-- Existing rows -->
        <template
          v-for="row in rows"
          :key="row.id">
          <!-- Edit-mode row -->
          <div
            v-if="isRowEditing(row) && editing"
            class="rounded-8 border-app-blue-600 bg-app-blue-50 p-px-16 gap-px-12 flex flex-col border"
            data-cy="custom-data-source-edit">
            <div class="gap-px-12 grid grid-cols-12">
              <TextInputNew
                v-model="editing.name"
                class="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Name"
                :required="true"
                :error-msg="nameError" />
              <!-- Type is locked after creation: changing it could silently invalidate projects
                   already referencing this source by type. -->
              <div
                class="col-span-12 sm:col-span-6 lg:col-span-4"
                data-cy="custom-data-source-edit-type-readonly">
                <TextInputNew
                  :model-value="editing.emissionFactorType ? formatEmissionFactorType(editing.emissionFactorType) : ''"
                  label="Type"
                  :required="true"
                  :disabled="true" />
                <p class="t-footnote text-type-muted mt-1">
                  Type can't be changed after creation; create a new datasource if you need a different type.
                </p>
              </div>
              <NumberInput
                :value="editing.emissionValue"
                class="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Emission value (kg CO₂ eq / kWh)"
                color="primary"
                :positive="true"
                :required="true"
                :align-right="true"
                :error-msg="emissionValueError"
                @update:value="(v: number | null) => editing && (editing.emissionValue = v)" />
              <div class="col-span-12">
                <TextFieldInput
                  :value="editing.description"
                  label="Description"
                  color="primary"
                  placeholder="Optional description"
                  size="min-h-20 w-full"
                  @update:value="(v: string) => editing && (editing.description = v)" />
              </div>
            </div>
            <div class="gap-px-8 flex justify-end">
              <Button
                label="Cancel"
                color="button"
                :disabled="isMutating"
                @click="cancelEdit" />
              <Button
                label="Save"
                color="primary"
                :is-loading="updateLoading"
                :disabled="isMutating"
                @click="submitEdit" />
            </div>
          </div>

          <!-- Read-mode row -->
          <div
            v-else
            class="rounded-8 gap-px-16 p-px-16 flex items-center justify-between border border-slate-200 bg-slate-50"
            data-cy="custom-data-source-row">
            <div class="gap-px-4 flex min-w-0 flex-col">
              <div class="gap-px-12 flex flex-wrap items-baseline">
                <span class="t-small-bold text-type-default">{{ row.name }}</span>
                <span class="t-caption text-type-muted">{{ formatEmissionFactorType(row.emissionFactorType) }}</span>
                <span class="t-caption text-type-muted">{{ Number(row.emissionValue) }} kg CO<sub>2</sub> eq / kWh</span>
              </div>
              <p
                v-if="row.description"
                class="t-caption-light text-type-muted line-clamp-2">
                {{ row.description }}
              </p>
              <p class="t-footnote text-type-muted">
                <span v-if="usageCountFor(row) === 0">Not in use</span>
                <span v-else>Used by {{ usageCountFor(row) }} project{{ usageCountFor(row) === 1 ? "" : "s" }}</span>
              </p>
            </div>
            <div class="gap-px-4 flex shrink-0 items-center">
              <button
                type="button"
                aria-label="Edit"
                title="Edit"
                :disabled="otherActionsDisabled"
                class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-1 transition-colors hover:bg-slate-100"
                :class="{ 'interactive-disabled': otherActionsDisabled }"
                @click="startEdit(row)">
                <Pencil
                  class="icon-sm text-type-muted"
                  :stroke-width="1.5" />
              </button>
              <button
                type="button"
                aria-label="Delete"
                title="Delete"
                :disabled="otherActionsDisabled"
                class="focus-ring interactive-active duration-fast ease-standard rounded-4 p-1 transition-colors hover:bg-slate-100"
                :class="{ 'interactive-disabled': otherActionsDisabled }"
                @click="confirmDelete(row)">
                <Trash2
                  class="icon-sm text-app-red-600"
                  :stroke-width="1.5" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </LoadingWrapper>
  </div>
</template>
